import type { AcquisitionBudget } from '@/store/slices/acquisitionBudget';

import type { BudgetOverride } from '../../mock-data/acquisition';

/**
 * Pure readiness/override derivation for the acquisition-budget slice record, kept
 * in its own module (only type-only imports) so it's testable without pulling in
 * `@/store` (which needs `window.config`, unavailable under jest's node env).
 *
 * `ready` reflects whether the GET has RESOLVED for this brand (the slice always
 * writes an entry on fulfilment, even an empty one for a brand with no budget yet,
 * see `unwrapBudget` / the 404-as-empty-record handling in `fetchAcquisitionBudget`),
 * not whether `acquisition_budget` happens to be a number. A brand with no budget yet
 * is a resolved "$0" state, not a still-loading one: treating it as not-ready left
 * the Budgets tiles (Remaining Budget / Daily Limit) and the Hero gauge skeletoning
 * forever for any brand that hasn't topped up. Mirrors `AdBudgetLimit`'s own
 * `budgetReady = budget !== undefined` for the same slice record.
 */
export const deriveBudgetOverride = (
  acqBudget: Partial<AcquisitionBudget> | undefined,
): { override: BudgetOverride | undefined; ready: boolean } => {
  const ready = acqBudget !== undefined;
  if (!ready || !acqBudget) return { ready, override: undefined };
  return {
    ready,
    override: {
      availableBudget: typeof acqBudget.acquisition_budget === 'number' ? acqBudget.acquisition_budget : 0,
      dailySpendLimit:
        typeof acqBudget.daily_acquisition_budget === 'number' ? acqBudget.daily_acquisition_budget : undefined,
    },
  };
};
